import { memo, useEffect, useState } from 'react'
import { FlexBetween, FlexCenter } from 'styles'
import {
  AmountLabel,
  BalanceLabel,
  BalanceValue,
  FormContent,
  MaxBtn,
  RangeTip,
  ExchangeCurrency,
} from './style'
import i18n from 'i18n'
import { useConvertBaseToken, useCurrentConvertInfo } from 'store/convert/hooks'
import { parseEtherToWei, parseWeiToEther, toGrouping } from 'utils/number'
import { useCurrencyBalance } from 'store/account/hooks'
import { useActionState } from 'store/app/hooks'
import Loading from 'components/Loading'
import InputAmount from 'components/InputAmount'
import { ConvertToken } from 'store/convert/reducer'
import { useDispatch } from 'react-redux'
import {
  updateBaseToken,
  updateCurrentConvertInfo,
  updateQuoteToken,
} from 'store/convert/actions'
import { Ether } from 'types'
import Decimal from 'decimal.js'

export const ExchangeAmount = memo(() => {
  const baseToken = useConvertBaseToken()
  const { token: selectedToken, amount } = baseToken
  const l2Balance = useCurrencyBalance(selectedToken?.name!)?.available ?? '0'
  const balancesPulling = useActionState('ethereumBalancesPulling')
  const { baseRange, price } = useCurrentConvertInfo()
  const [showTip, setShowTip] = useState<boolean>(false)
  const dispatch = useDispatch()

  const handleUpdateTokenInfo = (param: ConvertToken) => {
    setShowTip(false)
    if (baseRange?.maxSize && baseRange?.minSize && param.amount) {
      if (
        parseEtherToWei(param.amount).gt(parseEtherToWei(baseRange?.maxSize)) ||
        parseEtherToWei(param.amount).lt(parseEtherToWei(baseRange?.minSize))
      ) {
        setShowTip(true)
      }
    }
    param.amount && dispatch(updateQuoteToken({ amount: '' }))
    dispatch(updateBaseToken(param))
  }

  useEffect(() => {
    if (!price) return
    if (amount) {
      let _amount: string = ''
      _amount = Decimal.mul(amount, price).toFixed()

      dispatch(
        updateCurrentConvertInfo({
          baseAmount: amount,
          quoteAmount: _amount,
        }),
      )
    }
  }, [price, amount])

  const onAmount = (amount: string) => {
    if (!selectedToken) return

    let currentAmount: Ether = amount
    handleUpdateTokenInfo({ amount: currentAmount })
  }
  const setMaxAmount = () => {
    if (!selectedToken) return
    const currentAmount = parseWeiToEther(l2Balance)
    handleUpdateTokenInfo({ amount: currentAmount })
  }

  return (
    <>
      <FlexBetween>
        <AmountLabel>Convert Amount</AmountLabel>
        <FlexCenter>
          <BalanceLabel>
            {i18n.t('deposit-balance', {
              defaultValue: 'Balance',
            })}
            :{' '}
          </BalanceLabel>
          <BalanceValue>
            {selectedToken?.name
              ? toGrouping(parseWeiToEther(l2Balance), 6)
              : '0.00'}{' '}
            {selectedToken?.name}
            {balancesPulling ? (
              <>
                <Loading size={12} />
              </>
            ) : null}
          </BalanceValue>
        </FlexCenter>
      </FlexBetween>

      <FormContent
        sx={{
          padding: '0 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '16px',
        }}
      >
        <InputAmount
          value={amount ?? ''}
          placeholder={
            baseRange?.maxSize && baseRange?.minSize
              ? `${baseRange.minSize} ~ ${baseRange.maxSize}`
              : `Enter amount...`
          }
          onInput={selectedToken ? onAmount : () => {}}
        />
        <MaxBtn onClick={setMaxAmount}>
          {i18n.t('deposit-max', {
            defaultValue: 'MAX',
          })}
        </MaxBtn>
        <ExchangeCurrency>{selectedToken?.name}</ExchangeCurrency>
      </FormContent>
      {showTip ? (
        <RangeTip>{`Quantity range ${baseRange?.minSize}~${baseRange?.maxSize}`}</RangeTip>
      ) : null}
    </>
  )
})
